"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useRef } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormMessage } from "@/components/ui/form";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useJourneyData } from "@/hooks/use-journey-data";
import { cn } from "@/lib/utils";
import { SEARCH_SOURCES, type SearchSource } from "@/schemas/journey";
import { useToast } from "../../ui/use-toast";

const searchSourceSchema = z.object({
  searchSources: z
    .array(z.enum(["realtor.com", "redfin.com", "zillow.com"] as const))
    .min(1, "Select at least one search source"),
});

type SearchSourceForm = z.infer<typeof searchSourceSchema>;

// Helper to build search URLs with filters
function buildSearchUrl(
  source: string,
  filters: {
    city?: string;
    minBeds?: number;
    minBaths?: number;
    minPrice?: number;
    maxPrice?: number;
  },
) {
  const { city, minBeds, minBaths, minPrice, maxPrice } = filters;
  // Format city for different sites
  const realtorCity = city?.replace("-", "_").toUpperCase();
  const formattedCity = city
    ?.split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(", ");

  switch (source) {
    case "realtor.com":
      return `https://www.realtor.com/realestateandhomes-search/${realtorCity}/beds-${minBeds}/baths-${minBaths}/price-na-${maxPrice}`;

    case "redfin.com":
      return `https://www.redfin.com/city/5155/CO/Denver/filter/property-type=house+townhouse,min-beds=${minBeds},min-baths=${minBaths},max-price=${maxPrice}`;

    case "homes.com":
      return `https://www.homes.com/${city}/${minBeds}-to-5-bedroom/?bath-min=${minBaths}&price-max=${maxPrice}`;

    case "foreclosure.com":
      return `https://www.foreclosure.com/listing/search?q=${formattedCity}&pa=${minPrice}&pb=${maxPrice}&bdi=${minBeds}&bhi=${minBaths}&view=list`;

    case "forsalebyowner.com":
      return `https://www.forsalebyowner.com/search/list/${city}/${minBeds}-beds/house,townhouse-types/${minPrice}:${maxPrice}-price`;

    case "zillow.com":
      return `https://www.zillow.com/homes/${city}/${minBeds}-_beds/${minBaths}-_baths/${minPrice}-${maxPrice}_price/house,townhouse_type/`;

    default:
      return `https://${source}`;
  }
}

// Add this interface before SOURCE_INFO
interface SourceInfo {
  description: string;
  logo: string;
  fallbackLogo?: string;
}

const SOURCE_INFO: Record<keyof typeof SEARCH_SOURCES, SourceInfo> = {
  "realtor.com": {
    description: "Largest MLS database with comprehensive listing details",
    logo: "https://logo.clearbit.com/realtor.com",
    fallbackLogo: "https://logo.clearbit.com/nar.realtor",
  },
  "redfin.com": {
    description: "Modern interface with detailed property data and pricing",
    logo: "https://logo.clearbit.com/redfin.com",
  },
  "zillow.com": {
    description: "Most visited real estate website with Zestimate values",
    logo: "https://logo.clearbit.com/zillow.com",
  },
  // "homes.com": {
  //   description: "User-friendly search with extensive photo galleries",
  //   logo: "https://logo.clearbit.com/homes.com",
  // },
  // "foreclosure.com": {
  //   description: "Specialized in foreclosure and bank-owned properties",
  //   logo: "https://logo.clearbit.com/foreclosure.com",
  //   fallbackLogo: "https://logo.clearbit.com/auction.com",
  // },
  // "forsalebyowner.com": {
  //   description: "Direct-from-owner listings with potential savings",
  //   logo: "https://logo.clearbit.com/forsalebyowner.com",
  // },
};

export function SearchSourceSelector() {
  const { journeyData, updateJourney } = useJourneyData();
  const { toast } = useToast();
  const updateTimeoutRef = useRef<NodeJS.Timeout>();

  const form = useForm<SearchSourceForm>({
    resolver: zodResolver(searchSourceSchema),
    values: {
      searchSources: journeyData?.savedSearchSources || [],
    },
  });

  const handleSourceToggle = (source: SearchSource) => {
    const currentSources = form.getValues().searchSources;
    const newSources = currentSources.includes(source)
      ? currentSources.filter((s) => s !== source)
      : [...currentSources, source];

    form.setValue("searchSources", newSources);

    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    updateTimeoutRef.current = setTimeout(() => {
      updateJourney.mutate(
        { savedSearchSources: newSources },
        {
          onSuccess: () => {
            toast({
              title: "Search Sources Updated",
              description: "Your preferred search sources have been saved",
            });
          },
        },
      );
    }, 500);
  };

  return (
    <Form {...form}>
      <form>
        <FormField
          control={form.control}
          name="searchSources"
          render={({ field }) => (
            <FormItem>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Object.entries(SEARCH_SOURCES).map(([key, label]) => {
                  const info = SOURCE_INFO[key as keyof typeof SOURCE_INFO];
                  const isSelected = field.value.includes(key as SearchSource);
                  const searchUrl = buildSearchUrl(key, {
                    minBeds: journeyData?.minBedrooms ?? undefined,
                    minBaths: journeyData?.minBathrooms ?? undefined,
                    minPrice: journeyData?.budget
                      ? journeyData.budget * 0.8
                      : undefined,
                    maxPrice: journeyData?.budget ?? undefined,
                  });

                  const CardWrapper = isSelected ? Tooltip : "div";

                  return (
                    <CardWrapper key={key}>
                      {isSelected && (
                        <TooltipTrigger asChild>
                          <motion.div
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="relative"
                          >
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={() =>
                                handleSourceToggle(key as SearchSource)
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  window.open(searchUrl, "_blank");
                                }
                              }}
                              className={cn(
                                "w-full relative flex items-center gap-4 p-4 h-auto hover:bg-gray-50",
                                "border-2 transition-all duration-200 rounded-md",
                                isSelected
                                  ? "border-primary bg-primary/5 hover:bg-primary/10"
                                  : "group-hover:border-gray-300",
                              )}
                            >
                              <div className="shrink-0 w-12 h-12 rounded-lg bg-white shadow-sm flex items-center justify-center overflow-hidden">
                                <img
                                  src={info?.logo}
                                  alt={`${label} logo`}
                                  className="w-10 h-10 object-contain"
                                  onError={(e) => {
                                    if (info.fallbackLogo) {
                                      e.currentTarget.src = info.fallbackLogo;
                                    } else {
                                      e.currentTarget.src =
                                        "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='2' y='2' width='20' height='20' rx='2' ry='2'%3E%3C/rect%3E%3Cpath d='M12 6v12'%3E%3C/path%3E%3Cpath d='M6 12h12'%3E%3C/path%3E%3C/svg%3E";
                                      e.currentTarget.className =
                                        "w-6 h-6 text-gray-400";
                                    }
                                  }}
                                />
                              </div>
                              <div className="flex-1 text-left min-w-0">
                                <div className="font-semibold truncate">
                                  {label}
                                </div>
                                <div className="text-xs text-gray-500 line-clamp-2 mt-0.5">
                                  {info.description}
                                </div>
                              </div>
                              {isSelected && (
                                <div
                                  className="absolute top-2 right-2"
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      window.open(searchUrl, "_blank");
                                    }
                                  }}
                                >
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 hover:bg-primary/10"
                                    onClick={() =>
                                      window.open(searchUrl, "_blank")
                                    }
                                  >
                                    <ArrowUpRight className="w-4 h-4 text-primary" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </motion.div>
                        </TooltipTrigger>
                      )}
                      {!isSelected && (
                        <motion.div
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className="relative"
                        >
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={() =>
                              handleSourceToggle(key as SearchSource)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                window.open(searchUrl, "_blank");
                              }
                            }}
                            className={cn(
                              "w-full relative flex items-center gap-4 p-4 h-auto hover:bg-gray-50",
                              "border-2 transition-all duration-200 rounded-md",
                              isSelected
                                ? "border-primary bg-primary/5 hover:bg-primary/10"
                                : "group-hover:border-gray-300",
                            )}
                          >
                            <div className="shrink-0 w-12 h-12 rounded-lg bg-white shadow-sm flex items-center justify-center overflow-hidden">
                              <img
                                src={info?.logo}
                                alt={`${label} logo`}
                                className="w-10 h-10 object-contain"
                                onError={(e) => {
                                  if (info.fallbackLogo) {
                                    e.currentTarget.src = info.fallbackLogo;
                                  } else {
                                    e.currentTarget.src =
                                      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='2' y='2' width='20' height='20' rx='2' ry='2'%3E%3C/rect%3E%3Cpath d='M12 6v12'%3E%3C/path%3E%3Cpath d='M6 12h12'%3E%3C/path%3E%3C/svg%3E";
                                    e.currentTarget.className =
                                      "w-6 h-6 text-gray-400";
                                  }
                                }}
                              />
                            </div>
                            <div className="flex-1 text-left min-w-0">
                              <div className="font-semibold truncate">
                                {label}
                              </div>
                              <div className="text-xs text-gray-500 line-clamp-2 mt-0.5">
                                {info.description}
                              </div>
                            </div>
                            {isSelected && (
                              <div
                                className="absolute top-2 right-2"
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    window.open(searchUrl, "_blank");
                                  }
                                }}
                              >
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 hover:bg-primary/10"
                                  onClick={() =>
                                    window.open(searchUrl, "_blank")
                                  }
                                >
                                  <ArrowUpRight className="w-4 h-4 text-primary" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                      {isSelected && (
                        <TooltipContent side="top" className="max-w-xs">
                          <p>
                            Click to deselect, or use the arrow to search{" "}
                            {label}
                          </p>
                        </TooltipContent>
                      )}
                    </CardWrapper>
                  );
                })}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}
